import mixpanel from 'mixpanel-browser';
import {
  ActivityAssessmentAnswersDocument_,
  CompanyFilesDocument_,
  useRemoveFileMutation,
} from 'models';
import { nhost } from 'Providers/nhost/NhostProvider.tsx';
import { useCallback, useState } from 'react';
import { useCurrentCompanyId } from 'utils/hooks';
import { TRACKING_EVENTS } from 'utils/mixpanel';

const normalizeFileList = (files: File | File[] | FileList): File[] => {
  if (files instanceof File) {
    return [files];
  } else if (Array.isArray(files)) {
    return files;
  } else if (files instanceof FileList) {
    return Array.from(files);
  }
  return [];
};

export function useSanitizedFileUpload() {
  const secureUpload = useSecureFileUpload();
  const [id, setId] = useState<string | undefined>();
  const [isUploaded, setIsUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isError, setIsError] = useState(false);

  const upload = async (params: { file: File; bucketId?: string }) => {
    const { file, bucketId } = params;

    if (!file) {
      throw new Error('No file provided');
    }

    setIsUploading(true);
    setIsError(false);

    try {
      const result = await secureUpload.upload({
        file,
        bucketId,
      });

      setId(result.id);
      setIsUploaded(true);
      setIsUploading(false);

      return result;
    } catch (error) {
      setIsError(true);
      setIsUploading(false);
      throw error;
    }
  };

  return { upload, id, isUploaded, isUploading, isError };
}

export function useSanitizedMultipleFilesUpload() {
  const secureUpload = useSecureMultipleFilesUpload();
  const [files, setFiles] = useState<any[]>([]);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isError, setIsError] = useState(false);

  const upload = async (params: { files: File | File[] | FileList; bucketId?: string }) => {
    const { files: inputFiles, bucketId } = params;

    if (!inputFiles) {
      throw new Error('No files provided');
    }

    const normalizedFiles = normalizeFileList(inputFiles);
    setFiles(normalizedFiles);

    setIsUploading(true);
    setIsError(false);

    try {
      const result = await secureUpload.upload({
        files: normalizedFiles,
        bucketId,
      });

      setFiles(result.files);
      setIsUploaded(true);
      setIsUploading(false);

      return result;
    } catch (error) {
      setIsError(true);
      setIsUploading(false);
      throw error;
    }
  };

  return { upload, files, isUploaded, isUploading, isError };
}

export function useSecureFileUpload() {
  /*************  ✨ Windsurf Command ⭐  *************/
  /**
   * Uploads a file to the backend.
   * @param {Object} params - object containing the file to be uploaded and the bucket ID.
   * @param {File} params.file - the file to be uploaded.
   * @param {string} [params.bucketId='default'] - the bucket ID to upload the file to.
   * @returns {Promise<Object>} - a promise that resolves to an object containing the file ID, name, isUploaded flag, isUploading flag, isError flag, and a destroy function.
   */
  /*******  228cc800-f0de-404c-b114-8bf914a1be2d  *******/
  const uploadToBackend = async (params: { file: File; bucketId?: string }) => {
    const { file, bucketId = 'default' } = params;

    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const payload = {
      file: {
        name: file.name,
        type: file.type,
        size: file.size,
        data: base64,
      },
      bucketId,
    };

    const response = await nhost.functions.post<{ fileId: string; fileName: string }>(
      '/upload-file',
      payload
    );

    const data = response.body;
    return {
      id: data.fileId,
      name: data.fileName,
      isUploaded: true,
      isUploading: false,
      isError: false,
      destroy: () => {},
    };
  };

  return { upload: uploadToBackend };
}

export function useSecureMultipleFilesUpload() {
  const uploadToBackend = async (params: { files: File[]; bucketId?: string }) => {
    const { files, bucketId = 'default' } = params;

    const filePromises = files.map(async (file) => {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      return {
        name: file.name,
        type: file.type,
        size: file.size,
        data: base64,
      };
    });

    const fileData = await Promise.all(filePromises);

    const payload = {
      files: fileData,
      bucketId,
    };

    const response = await nhost.functions.post<{ files: { fileId: string; fileName: string }[] }>(
      '/upload-file',
      payload
    );

    const data = response.body;
    return {
      files: data.files.map((f) => ({
        id: f.fileId,
        name: f.fileName,
        isUploaded: true,
        isUploading: false,
        isError: false,
        destroy: () => {},
      })),
    };
  };

  return { upload: uploadToBackend };
}

export function useDeleteFile() {
  const { companyId } = useCurrentCompanyId();
  const [deleteDocumentationFile] = useRemoveFileMutation({
    refetchQueries: [CompanyFilesDocument_, ActivityAssessmentAnswersDocument_],
    awaitRefetchQueries: true,
  });

  return useCallback(
    async ({ id, storageFileId }: { id: string; storageFileId: string }) => {
      mixpanel.track(TRACKING_EVENTS.DRIVE.DELETE, {
        companyId,
        fileId: id,
      });
      await nhost.storage.deleteFile(storageFileId);

      return deleteDocumentationFile({
        variables: { id },
      }).then(({ data }) => data?.file);
    },
    [deleteDocumentationFile]
  );
}
